// ---------------------------------------------------------------------------
// My Tasks — the personal cross-project view, with CUSTOM, REARRANGEABLE buckets.
// ---------------------------------------------------------------------------
// The team asked for the Odoo "my tasks" feel: an auto inbox of everything assigned
// to me, split into sections I can name, reorder, add and remove (Upcoming, In
// Review, This Week, This Month, Complete, Pending, …). Buckets are RULE-based
// (smart), routed first-match in the user's chosen order; anything that matches no
// visible bucket lands in "Other" so no task is ever hidden. Layout persists
// server-side (per user), so it follows them across devices.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, Check, ChevronDown, ChevronUp, List, Plus, Settings, Star, Target, X } from 'lucide-react';
import type { TaskListItem, MyBucketConfig, MyBucketKey } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { completeWithUndo } from '@/features/tasks/mutations';
import { MyCalendar } from './MyCalendar';
import { useUiStore } from '@/core/ui/uiStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { prefetchTask, useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { useToastStore } from '@/core/ui/toastStore';
import { StateBadge, Skeleton } from '@/core/ui/atoms';
import { formatDate, relDueLabel } from '@/core/ui/helpers';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';
import { TaskContextMenu } from '@/features/tasks/components/TaskContextMenu';

const CLOSED = new Set(['1_done', '1_canceled']);
// Badge only the states that SAY something (mirrors the board). "In Progress" is the
// default condition of work — repeating it beside 131 rows was volume without signal.
const NOTABLE_STATE = new Set(['02_changes_requested', '03_approved', '04_waiting_normal', '1_canceled']);

const BUCKET_META: Record<MyBucketKey, { label: string; tone: string; hint: string }> = {
  overdue:    { label: 'Overdue',     tone: 'text-danger',    hint: 'Past its due date' },
  today:      { label: 'Today',       tone: 'text-warn',      hint: 'Due today' },
  this_week:  { label: 'This Week',   tone: 'text-warn',      hint: 'Due within 7 days' },
  this_month: { label: 'This Month',  tone: 'text-ink',       hint: 'Due in the next month' },
  upcoming:   { label: 'Upcoming',    tone: 'text-muted',     hint: 'Due more than a month out' },
  no_date:    { label: 'Pending',     tone: 'text-faint',     hint: 'No due date yet' },
  in_review:  { label: 'In Review',   tone: 'text-brand',     hint: 'Approved / changes requested / waiting' },
  blocked:    { label: 'Blocked',     tone: 'text-danger',    hint: 'Waiting on another task' },
  pending:    { label: 'In Progress', tone: 'text-muted',     hint: 'Actively in progress' },
  complete:   { label: 'Complete',    tone: 'text-brand',     hint: 'Recently finished' },
};
const ALL_KEYS = Object.keys(BUCKET_META) as MyBucketKey[];

// Hover-intent prefetch (instant-open layer 3): 100ms of dwell on a row warms
// the task-detail cache so opening the task renders with no spinner. One timer
// is enough — the pointer only hovers one row at a time. Touch-only devices
// never produce a useful mouseenter, so they skip the wiring entirely.
// typeof-guarded: the client unit tests import under NODE, where module-scope
// window access crashes the require before a single test runs.
const HOVERABLE = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
let hoverTimer: ReturnType<typeof setTimeout> | undefined;
const hoverStart = (id: number) => {
  if (!HOVERABLE) return;
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => prefetchTask(id), 100);
};
const hoverEnd = () => clearTimeout(hoverTimer);

/** Whole-day distance from today for a task's key date (deadline, else film date). */
function daysUntil(t: TaskListItem): number | null {
  const iso = t.deadline ?? t.filmDate;
  if (!iso) return null;
  const hasTime = iso.includes('T') || iso.includes(' ');
  const d = new Date(hasTime ? iso.replace(' ', 'T') : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  return Math.round((that.getTime() - today.getTime()) / 86_400_000);
}

/** Does a task belong in a given bucket rule? All non-'complete' rules are for OPEN
 *  tasks only, so a finished task only ever lands in 'complete'. */
function matches(key: MyBucketKey, t: TaskListItem): boolean {
  const closed = CLOSED.has(t.state);
  if (key === 'complete') return closed;
  if (closed) return false;
  const d = daysUntil(t);
  switch (key) {
    case 'overdue': return d !== null && d < 0;
    case 'today': return d === 0;
    case 'this_week': return d !== null && d > 0 && d <= 7;
    case 'this_month': return d !== null && d > 7 && d <= 31;
    case 'upcoming': return d !== null && d > 31;
    case 'no_date': return d === null;
    case 'in_review': return t.state === '02_changes_requested' || t.state === '03_approved' || t.state === '04_waiting_normal';
    case 'blocked': return t.blockedByCount > 0;
    case 'pending': return t.state === '01_in_progress';
    default: return false;
  }
}

export function MyWork() {
  const close = useUiStore((s) => s.closeMyWork);
  useEscapeToClose(close);
  const openTaskDetail = useTaskDetailStore((s) => s.open);
  const areas = useAreaStore((s) => s.areas);
  const toast = useToastStore((s) => s.show);

  // List or calendar — the SAME tasks, two shapes. Buckets answer "what should I
  // pick up now"; the calendar answers "what does my month look like". Persisted
  // because whichever you think in is a habit, not a per-visit decision.
  // Home can ask for a specific view ("▤ Calendar" should land ON the calendar).
  // Absent that, fall back to whatever you used last.
  const requestedView = useUiStore((s) => s.myWorkView);
  const [mode, setMode] = useState<'list' | 'calendar'>(() => {
    if (requestedView) return requestedView;
    try { return localStorage.getItem('cg.mywork.mode') === 'calendar' ? 'calendar' : 'list'; } catch { return 'list'; }
  });
  const [openTasks, setOpenTasks] = useState<TaskListItem[] | null>(null);
  // "Waiting on your OK" (NOW tier): cards sitting in approval stages of the
  // projects this person MANAGES, oldest first. Empty for non-reviewers —
  // and the section simply doesn't render (empty sections whisper).
  const [reviewQueue, setReviewQueue] = useState<Array<{ taskId: number; name: string; areaName: string; stageName: string; inStageSince: string | null; assignees: string[] }>>([]);
  const [closedTasks, setClosedTasks] = useState<TaskListItem[]>([]);
  const [config, setConfig] = useState<MyBucketConfig[] | null>(null);
  const [customizing, setCustomizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Right-click acts (§5D): the context menu writes through tasksStore, which
  // can't reach these local lists — bumping reloadSeq refetches them after any
  // verb (and after a toast Undo) so the rows tell the truth again.
  const [reloadSeq, setReloadSeq] = useState(0);
  const reload = useCallback(() => setReloadSeq((n) => n + 1), []);
  const [ctxMenu, setCtxMenu] = useState<{ task: TaskListItem; x: number; y: number } | null>(null);
  // Which sections have their "older than 3 months" band expanded (default hidden —
  // the graveyard is one line until someone asks for it).
  const [agedOpen, setAgedOpen] = useState<Record<string, boolean>>({});
  // Fold state for sections (`s:<key>`) and project groups (`g:<key>:<band>:<name>`),
  // persisted per browser — with 400 open tasks this page is unusable unless what you
  // fold STAYS folded. Absent key = the per-context default (big sections start with
  // their groups folded so the page opens as a table of contents, not a scroll).
  const [folds, setFolds] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('cgp:mywork:folds') || '{}') as Record<string, boolean>; } catch { return {}; }
  });
  const toggleFold = (k: string, def: boolean) => setFolds((s) => {
    const next = { ...s, [k]: !(s[k] ?? def) };
    try { localStorage.setItem('cgp:mywork:folds', JSON.stringify(next)); } catch { /* quota */ }
    return next;
  });

  // Tracks whether anything is on screen, so a failed background RELOAD (after a
  // context-menu verb) keeps the list instead of tearing it down with the error
  // screen — same discipline as tasksStore's hasCards guard.
  const hasTasksRef = useRef(false);
  useEffect(() => { hasTasksRef.current = openTasks !== null; }, [openTasks]);

  useEffect(() => {
    api.get<{ queue: Array<{ taskId: number; name: string; areaName: string; stageName: string; inStageSince: string | null; assignees: string[] }> }>('/tasks/review-queue')
      .then((r) => setReviewQueue(r.queue ?? []))
      .catch(() => setReviewQueue([]));
  }, [reloadSeq]);
  useEffect(() => {
    api.get<{ tasks: TaskListItem[] }>('/my-tasks')
      .then((r) => { setOpenTasks(r.tasks); setError(null); })
      .catch((e) => { if (!hasTasksRef.current) setError(e instanceof Error ? e.message : 'Could not load your work.'); });
  }, [reloadSeq]);
  useEffect(() => {
    api.get<{ buckets: MyBucketConfig[] }>('/my-tasks/config')
      .then((r) => setConfig(r.buckets))
      .catch(() => setConfig([])); // fall back to no custom buckets rather than blocking
  }, []);

  // Only pay for the closed-tasks fetch when a "Complete" bucket is actually shown.
  useEffect(() => {
    try { localStorage.setItem('cg.mywork.mode', mode); } catch { /* private mode */ }
  }, [mode]);

  // The calendar also wants FINISHED work: an empty square on a day you know you
  // shipped something reads as data loss, not as "nothing was due".
  const wantsClosed = mode === 'calendar' || (config ?? []).some((b) => b.key === 'complete');
  useEffect(() => {
    if (!wantsClosed) { setClosedTasks([]); return; }
    let alive = true;
    api.get<{ tasks: TaskListItem[] }>('/my-tasks/closed?limit=50')
      .then((r) => {
        if (!alive) return;
        // Merge, don't replace: a task the user just completed optimistically won't
        // be in this (pre-write) search yet — keep it so it doesn't blink out of the
        // Complete bucket until a remount. Same race applyServerTask guards on the board.
        const ids = new Set(r.tasks.map((t) => t.id));
        setClosedTasks((prev) => [...prev.filter((p) => CLOSED.has(p.state) && !ids.has(p.id)), ...r.tasks]);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [wantsClosed, reloadSeq]);

  // Bumped on EVERY local config change (a save OR a keystroke in a name field).
  // A PUT echo only wins if no newer local edit has happened since it was sent —
  // otherwise the server's copy of the pre-edit payload would wipe what the user
  // just typed, or an out-of-order response would rewind a reorder.
  const configSeq = useRef(0);
  const saveConfig = useCallback((next: MyBucketConfig[]) => {
    const at = ++configSeq.current;
    setConfig(next); // optimistic
    api.put<{ buckets: MyBucketConfig[] }>('/my-tasks/config', { buckets: next })
      .then((r) => { if (configSeq.current === at) setConfig(r.buckets); })
      .catch(() => toast('Could not save your layout — try again.', 'error'));
  }, [toast]);

  const completeTask = async (t: TaskListItem) => {
    setOpenTasks((prev) => (prev ?? []).filter((x) => x.id !== t.id));
    const done: TaskListItem = { ...t, state: '1_done' };
    if (wantsClosed) setClosedTasks((prev) => [done, ...prev.filter((x) => x.id !== t.id)]);
    const ok = await completeWithUndo(t.id, t.name, t.state, () => {
      setOpenTasks((prev) => [t, ...(prev ?? []).filter((x) => x.id !== t.id)]);
      setClosedTasks((prev) => prev.filter((x) => x.id !== t.id));
    });
    if (!ok) {
      setOpenTasks((prev) => [t, ...(prev ?? []).filter((x) => x.id !== t.id)]);
      setClosedTasks((prev) => prev.filter((x) => x.id !== t.id));
    }
  };

  // My Work spans projects you may not be a member of (task-level access); prefer the
  // name the server ships and fall back to the store only for older payloads.
  const areaName = (t: TaskListItem) => t.areaName || areas.find((a) => a.id === t.areaId)?.name || '';

  // Route every task into the FIRST bucket (in config order) whose rule matches;
  // leftovers go to "Other" so nothing is ever hidden.
  const rendered = useMemo(() => {
    const cfg = config ?? [];
    const all = [...(openTasks ?? []), ...closedTasks];
    const byKey = new Map<string, TaskListItem[]>();
    const other: TaskListItem[] = [];
    for (const t of all) {
      const hit = cfg.find((b) => matches(b.key, t));
      if (hit) (byKey.get(hit.key) ?? byKey.set(hit.key, []).get(hit.key)!).push(t);
      else if (!CLOSED.has(t.state)) other.push(t); // closed tasks with no Complete bucket simply don't show
    }
    const sections: Array<{ key: string; name: string; tone: string; rows: TaskListItem[] }> = cfg
      .map((b) => ({ key: b.key as string, name: b.name, tone: BUCKET_META[b.key].tone, rows: byKey.get(b.key) ?? [] }))
      .filter((s) => s.rows.length > 0);
    if (other.length) sections.push({ key: 'other', name: 'Other', tone: 'text-faint', rows: other });
    return sections;
  }, [config, openTasks, closedTasks]);

  const usedKeys = new Set((config ?? []).map((b) => b.key));
  const addable = ALL_KEYS.filter((k) => !usedKeys.has(k));
  const move = (i: number, dir: -1 | 1) => {
    const cfg = [...(config ?? [])];
    const j = i + dir;
    if (j < 0 || j >= cfg.length) return;
    [cfg[i], cfg[j]] = [cfg[j], cfg[i]];
    saveConfig(cfg);
  };

  const openCount = openTasks?.length ?? 0;

  return (
    <div className="fixed inset-0 z-[55] flex flex-col bg-page animate-fade-in">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-4">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><Icon i={Target} size={14} /> My Tasks</span>
        {openTasks && <span className="text-xs text-faint">{openCount} open task{openCount === 1 ? '' : 's'} assigned to you</span>}
        <div className="ml-auto flex items-center gap-1 rounded-md border border-line p-0.5">
          {(['list', 'calendar'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex items-center gap-1 rounded px-2 py-0.5 text-[11px] transition ${mode === m ? 'bg-brand-soft text-ink' : 'text-muted hover:bg-hover hover:text-ink'}`}
              title={m === 'list' ? 'Grouped into your buckets' : 'Your work across every project, on one month'}
            >
              {m === 'list' ? <><Icon i={List} size={14} /> List</> : <><Icon i={CalendarDays} size={14} /> Calendar</>}
            </button>
          ))}
        </div>
        <button
          onClick={() => setCustomizing((v) => !v)}
          className={`flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition ${customizing ? 'border-brand text-brand' : 'border-line text-muted hover:bg-hover hover:text-ink'}`}
          title="Customize your buckets"
        ><Icon i={Settings} size={14} /> Customize</button>
        <button onClick={close} className="rounded p-1 text-muted hover:bg-hover hover:text-ink" title="Close"><Icon i={X} size={14} /></button>
      </header>

      {mode === 'calendar' ? (
        <div className="min-h-0 flex-1">
          <MyCalendar tasks={[...(openTasks ?? []), ...closedTasks]} />
        </div>
      ) : (
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 py-4">
          {reviewQueue.length > 0 && (
            <section className="mb-5 rounded-lg border border-brand/40 bg-brand/[0.06] p-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[12px] font-semibold text-brand">Waiting on your OK · {reviewQueue.length}</span>
                <span className="text-[11px] text-faint">oldest first — these can’t move until you look</span>
              </div>
              <div className="overflow-hidden rounded-md border border-line bg-canvas">
                {reviewQueue.map((r) => (
                  <div
                    key={r.taskId}
                    onClick={() => { void openTaskDetail(r.taskId).then((ok) => { if (ok) close(); }); }}
                    className="flex w-full cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-3 py-2 last:border-0 hover:bg-hover"
                  >
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{r.name}</span>
                    <span className="text-[11px] text-faint">{r.areaName} · {r.stageName}</span>
                    {r.assignees.length > 0 && <span className="text-[11px] text-faint">{r.assignees.map((n) => n.split(' ')[0]).join(', ')}</span>}
                    {r.inStageSince && <span className="text-[11px] tabular-nums text-warn" title="Days since anyone touched this card">{Math.max(0, Math.floor((Date.now() - new Date(r.inStageSince.includes('T') ? r.inStageSince : `${r.inStageSince.replace(' ', 'T')}Z`).getTime()) / 86_400_000))}d quiet</span>}
                  </div>
                ))}
              </div>
            </section>
          )}
          {customizing && config && (
            <section className="mb-5 rounded-lg border border-brand/30 bg-brand/[0.04] p-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[12px] font-semibold text-ink">Your buckets</span>
                <span className="text-[11px] text-faint">reorder, rename, add or remove — saved automatically</span>
                <button onClick={() => saveConfig([])} className="ml-auto text-[11px] text-muted underline-offset-2 hover:text-ink hover:underline" title="Reset to the default layout">Reset</button>
              </div>
              <div className="space-y-1">
                {config.map((b, i) => (
                  <div key={b.key} className="flex items-center gap-2 rounded-md border border-line bg-canvas px-2 py-1">
                    <div className="flex flex-col leading-none">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-muted hover:text-ink disabled:opacity-25" title="Move up"><Icon i={ChevronUp} size={14} /></button>
                      <button onClick={() => move(i, 1)} disabled={i === config.length - 1} className="text-muted hover:text-ink disabled:opacity-25" title="Move down"><Icon i={ChevronDown} size={14} /></button>
                    </div>
                    <input
                      value={b.name}
                      onChange={(e) => { configSeq.current++; setConfig((prev) => (prev ?? []).map((x, j) => (j === i ? { ...x, name: e.target.value.slice(0, 40) } : x))); }}
                      onBlur={() => saveConfig(config)}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                      className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none"
                    />
                    <span className="shrink-0 text-[10px] text-faint">{BUCKET_META[b.key].hint}</span>
                    <button onClick={() => saveConfig(config.filter((_, j) => j !== i))} className="shrink-0 text-muted hover:text-danger" title="Remove this bucket"><Icon i={X} size={14} /></button>
                  </div>
                ))}
              </div>
              {addable.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-faint">Add:</span>
                  {addable.map((k) => (
                    <button
                      key={k}
                      onClick={() => saveConfig([...(config ?? []), { key: k, name: BUCKET_META[k].label }])}
                      className="flex items-center gap-1 rounded-full border border-line px-2 py-0.5 text-[11px] text-muted transition hover:border-brand hover:text-brand"
                      title={BUCKET_META[k].hint}
                    ><Icon i={Plus} size={14} /> {BUCKET_META[k].label}</button>
                  ))}
                </div>
              )}
            </section>
          )}

          {error && <div className="py-8 text-center text-sm text-danger">{error}</div>}
          {(!openTasks || !config) && !error && (
            // Boot ghost — three bucket sections in their real shape (header
            // line + carded 36px rows) so the swap to live buckets doesn't jump.
            <div aria-hidden>
              {[0, 1, 2].map((section) => (
                <section key={section} className="mb-5">
                  <div className="mb-1 flex items-center gap-2 px-2 py-0.5">
                    <Skeleton className="h-4 w-28" />
                  </div>
                  <div className="overflow-hidden rounded-lg border border-line bg-canvas">
                    {[0, 1, 2, 3].map((row) => (
                      <div key={row} className="flex items-center gap-3 border-b border-line px-3 py-2 last:border-0">
                        <Skeleton className="h-5 w-5 rounded-full" />
                        <Skeleton className="h-4 w-2/5" />
                        <Skeleton className="ml-auto h-4 w-16" />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
          {openTasks && config && rendered.length === 0 && (
            <div className="py-12 text-center text-sm text-faint">Nothing assigned to you right now — enjoy it while it lasts. 🌴</div>
          )}

          {rendered.map((section) => {
            // ORGANIZATION, two levels ("all this shit just thrown on a page" — Pat, 7/27):
            // 1. AGE BAND (overdue only): anything more than ~3 months late is a fact of
            //    history, not today's work — it collapses to one "Older · N" line so the
            //    graveyard stops burying the recent, actionable misses.
            // 2. PROJECT GROUPS: within a band, rows group under a project header, biggest
            //    group first — "the mess is mostly Content Calendar" becomes something the
            //    page SAYS instead of something you deduce from 97 repeated cells. Small
            //    buckets (≤5 rows or one project) stay flat: headers would outweigh rows.
            const ANCIENT_DAYS = -92;
            const isOverdue = section.key === 'overdue';
            const recent = isOverdue ? section.rows.filter((t) => (daysUntil(t) ?? 0) >= ANCIENT_DAYS) : section.rows;
            const ancient = isOverdue ? section.rows.filter((t) => (daysUntil(t) ?? 0) < ANCIENT_DAYS) : [];
            const ancientShown = agedOpen[section.key] ?? false;

            const renderRow = (t: TaskListItem, mutedTone: boolean, showProject: boolean) => {
              const due = t.deadline ?? t.filmDate;
              const isClosed = CLOSED.has(t.state);
              const rel = due ? relDueLabel(due) : '';
              const abs = due ? formatDate(due) : '';
              return (
                <div
                  key={t.id}
                  // Close only if it opened — open() refuses when the user keeps
                  // an unsaved draft, and this list is their way back in.
                  onClick={() => { void openTaskDetail(t.id).then((ok) => { if (ok) close(); }); }}
                  onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ task: t, x: e.clientX, y: e.clientY }); }}
                  onMouseEnter={() => hoverStart(t.id)}
                  onMouseLeave={hoverEnd}
                  className="flex w-full cursor-pointer items-center gap-3 border-b border-line px-3 py-2 text-left last:border-0 hover:bg-hover"
                >
                  {!isClosed ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); void completeTask(t); }}
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-line text-transparent transition hover:border-brand hover:text-brand"
                      title="Mark complete"
                    ><Icon i={Check} size={14} /></button>
                  ) : (
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-brand/40 text-brand" title="Completed"><Icon i={Check} size={14} /></span>
                  )}
                  {t.priority === '1' && <span className="shrink-0 text-warn" title="High priority"><Icon i={Star} size={14} className="fill-current" /></span>}
                  <span className={`min-w-0 flex-1 truncate text-[13px] ${isClosed ? 'text-muted line-through' : 'text-ink'}`}>{t.name}</span>
                  {t.blockedByCount > 0 && !isClosed && <span className="shrink-0 text-[11px] font-medium text-danger">blocked</span>}
                  {/* Fixed columns; project cell only in FLAT lists (grouped rows carry it
                      in their header). Badges only for notable states. Ancient rows mute
                      the lateness — the band header already said "old", 97 repetitions of
                      "12mo late" in danger-tone was the noise this section existed to cut. */}
                  {showProject && <span className="w-36 shrink-0 truncate text-right text-[11px] text-faint">{areaName(t)}</span>}
                  <span className={`w-[72px] shrink-0 text-right text-[11px] tabular-nums ${mutedTone ? 'text-faint' : section.tone}`}>{rel}</span>
                  <span className="hidden w-[70px] shrink-0 text-right text-[11px] tabular-nums text-faint sm:block">{rel !== abs ? abs : ''}</span>
                  <span className="flex w-[74px] shrink-0 justify-end">
                    {NOTABLE_STATE.has(t.state) && <StateBadge state={t.state} />}
                  </span>
                </div>
              );
            };

            // Big sections open as a TABLE OF CONTENTS: their project groups start
            // folded (a header line + count each), so 400 assigned tasks read as ~30
            // lines you open on demand instead of a scroll that never ends.
            const groupsDefaultFolded = section.rows.length > 12;
            const sectionFolded = folds[`s:${section.key}`] ?? false;

            const renderBand = (rows: TaskListItem[], mutedTone: boolean, bandKey: string) => {
              const projects = new Map<string, TaskListItem[]>();
              for (const t of rows) { const k = areaName(t) || '—'; projects.set(k, [...(projects.get(k) ?? []), t]); }
              if (rows.length <= 5 || projects.size <= 1) return rows.map((t) => renderRow(t, mutedTone, true));
              return [...projects.entries()]
                .sort((a, b) => b[1].length - a[1].length)
                .map(([name, rws]) => {
                  const gk = `g:${section.key}:${bandKey}:${name}`;
                  const open = !(folds[gk] ?? groupsDefaultFolded);
                  return (
                    <div key={name}>
                      <button
                        onClick={() => toggleFold(gk, groupsDefaultFolded)}
                        className="flex w-full items-center gap-2 border-b border-line bg-page/40 px-3 py-1.5 text-left transition last:border-0 hover:bg-hover"
                        title={open ? `Collapse ${name}` : `Expand ${name}`}
                      >
                        <span className="text-[10px] text-faint">{open ? '▾' : '▸'}</span>
                        <span className="text-[11px] font-semibold text-muted">{name}</span>
                        <span className="ml-auto text-[10px] tabular-nums text-faint">{rws.length}</span>
                      </button>
                      {open && rws.map((t) => renderRow(t, mutedTone, false))}
                    </div>
                  );
                });
            };

            return (
              <section key={section.key} className="mb-5">
                <button
                  onClick={() => toggleFold(`s:${section.key}`, false)}
                  className="mb-1 flex w-full items-center gap-2 rounded px-2 py-0.5 text-left transition hover:bg-hover"
                  title={sectionFolded ? `Expand ${section.name}` : `Collapse ${section.name}`}
                >
                  <span className="text-[10px] text-faint">{sectionFolded ? '▸' : '▾'}</span>
                  <span className={`text-[11px] font-semibold uppercase tracking-wide ${section.tone}`}>
                    {section.name} · {section.rows.length}
                  </span>
                </button>
                {!sectionFolded && (
                  <div className="overflow-hidden rounded-lg border border-line bg-canvas">
                    {renderBand(recent, false, 'r')}
                    {ancient.length > 0 && (
                      <>
                        <button
                          onClick={() => setAgedOpen((s) => ({ ...s, [section.key]: !ancientShown }))}
                          className="flex w-full items-center justify-center gap-2 border-b border-line px-3 py-2 text-[12px] text-faint transition last:border-0 hover:bg-hover hover:text-ink"
                        >
                          <span>{ancientShown ? '▾' : '▸'}</span>
                          <span>Older than 3 months · {ancient.length}</span>
                          <span className="text-[10px]">{ancientShown ? 'hide' : 'show'}</span>
                        </button>
                        {ancientShown && renderBand(ancient, true, 'a')}
                      </>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
      )}
      {/* Inside the z-[55] overlay's stacking context, so the menu's z-40/z-50
          layers sit ABOVE this screen, not under it. */}
      {ctxMenu && (
        <TaskContextMenu
          task={ctxMenu.task}
          position={{ x: ctxMenu.x, y: ctxMenu.y }}
          onClose={() => setCtxMenu(null)}
          onMutated={reload}
          onOpened={close}
        />
      )}
    </div>
  );
}
